import { useToast } from '@onefootprint/ui';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { getLogger } from '@/idv2/utils/logger';
import { useImgProcessorsContext } from '../../components/image-processors';
import { bytesToMegabytes, hasFileReaderSupport, isFileOrBlob, isString } from '../../utils/capture';
import compressImage, { COMPRESS_EXTRA_MAX_SIZE_MB } from './utils/compress-image';
import imageFileToStrippedBase64 from './utils/image-file-to-stripped-base64';
import resizeImage from './utils/resize-image';

type ProcessedImageFile = { file: File | Blob; extraCompressed: boolean };
type HandleError = (step: ImageProcessingStepError, error?: unknown) => void;
type ImageProcessors = ReturnType<typeof useImgProcessorsContext>;
enum ImageProcessingStepError {
  heic = 'heic',
  resize = 'resize',
  compress = 'compress',
  other = 'other',
  final = 'final',
}

export const DEFAULT_ACCEPTED_FILE_FORMATS = [
  '.jpg',
  '.jpeg',
  'image/jpeg',
  '.png',
  'image/png',
  '.webp',
  'image/webp',
  '.bmp',
  'image/bmp',
];

export enum ImageFileOrUrlProcessingError {
  heicNotSupported = 'heicNotSupported',
  compressionFailed = 'compressionFailed',
  other = 'other',
}

export type ProcessingErrorResult = {
  error: ImageFileOrUrlProcessingError;
};

export function isProcessingResultError(
  result: ProcessedImageFile | ProcessingErrorResult,
): result is ProcessingErrorResult {
  return 'error' in result;
}

const isError = (x: unknown): x is Error => x instanceof Error;
const isHeicType = (x: unknown) => x === 'image/heic' || x === 'image/heif';

const { logWarn, logInfo } = getLogger({
  name: 'idv2',
  location: 'use-process-image',
});

const stringify = (x: unknown): string => {
  try {
    return isString(x) ? x : JSON.stringify(x);
  } catch (e: unknown) {
    if (isError(e)) {
      logWarn(`Error during JSON.stringify: ${e?.message}`, e);
    }
    return '';
  }
};

const convertImageFileToStrippedBase64 = async (onError: HandleError, file: File): Promise<string | undefined> => {
  try {
    return await imageFileToStrippedBase64(file);
  } catch (error) {
    onError(ImageProcessingStepError.other, error);
    return undefined;
  }
};

const stepHeicConversion = async (
  onError: HandleError,
  imageProcessors: ImageProcessors | undefined,
  file: File,
): Promise<File | Error> => {
  try {
    if (imageProcessors?.heic && !imageProcessors.heicLoading) {
      // Resize and compress re-encode from quality 1 and lower it only to fit the upload budget,
      // so converting below 1 here drops detail the budget would have kept.
      const jpeg = await imageProcessors.heic({ blob: file, quality: 1 });
      return new File([jpeg], file.name, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }

    logWarn('HEIC image processor not available');
    return new Error('HEIC image processor not available');
  } catch (e: unknown) {
    onError(ImageProcessingStepError.heic, e);
    return isError(e) ? e : new Error('HEIC processing failed');
  }
};

const stepImageResize = async (onError: HandleError, file: File): Promise<File | Blob | Error> => {
  try {
    const resized = await resizeImage(file);
    return resized || new Error('resize processing failed');
  } catch (e) {
    onError(ImageProcessingStepError.resize, e);
    return isError(e) ? e : new Error('resize processing failed');
  }
};

const stepImageCompression = async (
  onError: HandleError,
  file: File | Blob,
  extraCompress: boolean = false,
): Promise<File | Error> => {
  try {
    const compressed = await compressImage(file, extraCompress);
    return compressed || new Error('compress processing failed');
  } catch (e) {
    onError(ImageProcessingStepError.compress, e);
    return isError(e) ? e : new Error('compress processing failed');
  }
};

/**
 * Processes a file by performing HEIC conversion, image resizing, and compression.
 *
 * @param {HandleError} onError - Function to handle errors.
 * @param {ImageProcessors | undefined} imageProcessors - Image processors configuration.
 * @param {File} file - The input file to be processed.
 * @param {boolean} [extraCompressFlag] - Optional flag for extra compression.
 * @returns {Promise<ProcessedImageFile | undefined>} A Promise that resolves to the processed file
 * or undefined if an error occurs during processing.
 *
 * @typedef {Object} ProcessedImageFile - Object containing the processed file and extra compression flag.
 * @property {File} file - The processed file.
 * @property {boolean} extraCompressed - Indicates if extra compression was applied.
 */
const normalizeFileType = async (file: File): Promise<File> => {
  try {
    const { fileTypeFromBuffer } = await import('file-type');
    const buffer = new Uint8Array(await file.slice(0, 4100).arrayBuffer());
    const detected = await fileTypeFromBuffer(buffer);
    if (!detected || detected.mime === file.type) {
      return file;
    }
    logWarn(`MIME mismatch: browser reported ${file.type}, magic bytes detected ${detected.mime}`);
    return new File([file], file.name, {
      type: detected.mime,
      lastModified: file.lastModified,
    });
  } catch (error) {
    logWarn(`Magic-byte detection failed, proceeding with browser-reported type: ${file.type}`, error);
    return file;
  }
};

const runProcessFileScript = async (
  onError: HandleError,
  imageProcessors: ImageProcessors | undefined,
  file: File,
  extraCompressFlag?: boolean,
): Promise<ProcessedImageFile | ProcessingErrorResult> => {
  // Reported at every exit, so upload volume by file type stays countable on all paths.
  const source = `${bytesToMegabytes(file.size)} MB ${file.type}`;
  const correctedFile = await normalizeFileType(file);

  if (correctedFile.type === 'application/pdf') {
    logInfo(`file processed: ${source} -> pdf, passed through unmodified`);
    return { file: correctedFile, extraCompressed: false };
  }

  const heicOutput = isHeicType(correctedFile.type)
    ? await stepHeicConversion(onError, imageProcessors, correctedFile)
    : correctedFile;

  if (isError(heicOutput)) {
    logWarn(`HEIC conversion failed, halting processing: ${source}`);
    return { error: ImageFileOrUrlProcessingError.heicNotSupported };
  }

  const resizeInput = isFileOrBlob(heicOutput) ? heicOutput : correctedFile;
  let resizeOutput: File | Blob | Error;
  // resizeImage is not supported for any file type other than the ones in DEFAULT_ACCEPTED_FILE_FORMATS. The promise is never resolved or rejected
  if (DEFAULT_ACCEPTED_FILE_FORMATS.includes(resizeInput.type)) {
    resizeOutput = hasFileReaderSupport()
      ? await stepImageResize(onError, resizeInput)
      : new Error('Unsupported FileReader in this browser');
  } else {
    resizeOutput = resizeInput;
  }

  if (isError(resizeOutput)) {
    logWarn(`Proceeding with not resized image: ${stringify(resizeOutput)}`);
  }

  const prevFile = [resizeOutput, heicOutput].find(isFileOrBlob);
  const compressionInput = prevFile && !isError(prevFile) ? prevFile : correctedFile;
  const compressOutput = await stepImageCompression(onError, compressionInput, extraCompressFlag);

  if (isError(compressOutput)) {
    logWarn(`Proceeding with uncompressed image: ${stringify(compressOutput)}`);
  }

  const extraCompressed =
    !isError(resizeOutput) &&
    !isError(compressOutput) &&
    resizeOutput.size > compressOutput.size &&
    Boolean(extraCompressFlag) &&
    compressOutput.size <= COMPRESS_EXTRA_MAX_SIZE_MB;

  const sizeInMb = (output: File | Blob | Error) => (isError(output) ? 'n/a' : bytesToMegabytes(output.size));
  logInfo(
    `file processed: ${source} -> resized ${sizeInMb(resizeOutput)} MB -> compressed ${sizeInMb(compressOutput)} MB, extra compressed: ${extraCompressed}`,
  );

  const final = [compressOutput, resizeOutput, heicOutput].find(isFileOrBlob);
  if (!final || isError(final)) {
    onError(ImageProcessingStepError.final);
  }
  return final && !isError(final) ? { file: final, extraCompressed } : { error: ImageFileOrUrlProcessingError.other };
};

const processImageUrl = async (
  onError: HandleError,
  url: string,
  extraCompress?: boolean,
): Promise<ProcessedImageFile | ProcessingErrorResult> => {
  let file: File | undefined;
  try {
    file = await imageCompression.getFilefromDataUrl(url, 'imageFileName');
  } catch (error) {
    onError(ImageProcessingStepError.other, error);
  }
  if (!file) {
    logWarn('Image file is undefined after image compression');
    return { error: ImageFileOrUrlProcessingError.other };
  }
  logInfo(`(getFilefromDataUrl) file from url: ${bytesToMegabytes(file.size)} MB ${file.type}`);

  const output = await runProcessFileScript(onError, undefined, file, extraCompress);
  return output;
};

const useProcessImage = (options?: { allowPdf?: boolean }) => {
  const { toast } = useToast();
  const imageProcessors = useImgProcessorsContext();
  const { t } = useTranslation('idv2', {
    keyPrefix: 'document-flow.components.image-file-url-processing-errors',
  });
  const handleError = (step: ImageProcessingStepError, error?: unknown) => {
    if (error) {
      logWarn(stringify(error), error);
    }
    toast({
      title: 'Uh-oh',
      description: t($ => $[step]),
    });
  };

  const acceptedFileFormats = [...DEFAULT_ACCEPTED_FILE_FORMATS];
  if (imageProcessors?.heic) {
    acceptedFileFormats.push('.heic', '.heif', 'image/heic', 'image/heif');
  }
  if (options?.allowPdf) {
    acceptedFileFormats.push('.pdf', 'application/pdf');
  }

  return {
    convertImageFileToStrippedBase64: (file: File) => convertImageFileToStrippedBase64(handleError, file),
    processImageFile: (file: File, extraCompressFlag?: boolean) =>
      runProcessFileScript(handleError, imageProcessors, file, extraCompressFlag),
    processImageUrl: (url: string, extraCompress?: boolean) => processImageUrl(handleError, url, extraCompress),
    acceptedFileFormats,
  };
};

export default useProcessImage;
